'use client';
import 'reflect-metadata';
import {
    AddressInputDto,
    Language,
    OrderApiDto,
    OrderPriceModifierType,
    OrderStatusDefinitionIdentifier,
} from '@order-mage/integration-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useLocale, useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, Fragment, useContext, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
    CartContext,
    useCartStore,
    buildPackageItemByIdMap,
    getApplicableTips,
    enrichPackageWithPriceableItem,
    isPackageApplicable,
    mapCartAddonsToOrderAddons,
    shouldIncludePackages,
} from '@/entities/cart';
import { useTrackedOrders } from '@/entities/order/lib/useTrackedOrders';
import { useStorefrontItemsIndex, useStorefrontCartMenuData } from '@/entities/product/lib/useStorefrontMenu';
import { isDeliveryMethod } from '@/entities/venue/lib/isDeliveryMethod';
import { useDeliveryMethodAvailability } from '@/entities/venue/lib/useDeliveryMethodAvailability';
import { getVenueReopenWhenText } from '@/entities/venue/lib/venue-availability-text.utils';
import { VenueAvailabilityKind } from '@/entities/venue/lib/venue-availability.utils';
import { useVenueAvailabilityStore } from '@/entities/venue/model/venue-availability.store';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { getCachedTelephoneVerified } from '@/features/auth/lib/useTelephoneVerification';
import { getOrderLandingStatusQueryKey } from '@/features/order/lib/order-landing.utils';
import { QueryApi } from '@/features/query/api';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { useRouter } from '@/shared/i18n/navigation';
import type { MessageKey } from '@/shared/i18n/translate';
import { cn } from '@/shared/lib/cn';
import { getFirebaseDeviceToken } from '@/shared/lib/compat';
import { EventService } from '@/shared/lib/event-service';
import { getLocaleDate, getLocaleTime } from '@/shared/lib/format-locale-date-time.util';
import { getLocalizedValue as localize } from '@/shared/lib/getLocalizedValue';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { Alert } from '@/shared/ui/alert';
import { AnimatedPrice } from '@/shared/ui/animated-price';
import { As } from '@/shared/ui/as';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { CartSummaryBoxItem } from './cart-summary-box-item';
import './cart-summary-box.scss';
import { SummaryItemT } from './types';

// Note: DISCOUNTS_MOCK is currently unused but kept for potential future use
// If needed, it should be moved inside the component to use t() hook
const DISCOUNTS_MOCK: SummaryItemT[] = [
    {
        name: 'Uplatněný kód',
        price: {
            amount: -200,
            currency: Currency.CZK,
        },
    },
    {
        name: 'Sleva na dopravu',
        price: {
            amount: -20,
            currency: Currency.CZK,
        },
    },
];

type Props = { defaultIsDetailsVisible?: boolean } & ComponentPropsWithoutRef<typeof Card>;
export const CartSummaryBox = ({ defaultIsDetailsVisible = false, ...props }: Props) => {
    const t = useTranslations();
    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const locale = useLocale();
    const router = useRouter();
    const getPageUrl = useGetPageUrl();
    const queryClient = useQueryClient();
    const { formMethods, getCartFormDefaultValues } = useContext(CartContext);
    const { verifyTelephoneEnabled, firstOrderDiscountEnabled } = useContext(AppConfigContext);
    const [selectedCurrency] = useSelectedCurrency();
    const itemsLookup = useStorefrontItemsIndex();
    const cartMenuData = useStorefrontCartMenuData();
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const createOrderMutation = useMutation(QueryApi.Order.mutations.Create);
    const [isDetailsVisible, setIsDetailsVisible] = useState(defaultIsDetailsVisible);
    const [_, setTrackedOrders] = useTrackedOrders();
    const venueAvailability = useVenueAvailabilityStore((s) => s.status);
    const isVenueOpen = venueAvailability.kind === VenueAvailabilityKind.Open;
    // Whether availability has been ANSWERED, which is a different question from
    // what the answer is. `ClosedUnknown` is a real answer — "closed, with no
    // next opening known", which is what a venue carrying no opening hours
    // resolves to — so reading it as "not loaded yet" left a genuinely closed
    // venue with a disabled submit and no explanation on it.
    const isVenueAvailabilityReady = useVenueAvailabilityStore((s) => s.hasComputed);
    const getLocalizedValue = useGetLocalizedValue();

    const currency = Currency.CZK;
    const itemsSum = useCartStore((s) => s.itemsSum);
    const packagesSum = useCartStore((s) => s.packagesSum);
    const subtotal = useCartStore((s) => s.subtotal);
    const deliveryPrice = useCartStore((s) => s.deliveryPrice);
    const matchedArea = useCartStore((s) => s.matchedArea);
    const selectedDeliveryMethod = useCartStore((s) => s.deliveryMethod);
    const tipsCalculation = useCartStore((s) => s.tips);
    const tipsTotal = useCartStore((s) => s.tipsTotal);
    const paymentMethodFee = useCartStore((s) => s.paymentMethodFee);
    const topUpFee = useCartStore((s) => s.topUpFee);
    const { getAvailability } = useDeliveryMethodAvailability();
    const total = useCartStore((s) => s.roundedTotal);
    const rounding = useCartStore((s) => s.rounding);

    // TODO: Remove — dočasná sleva na první objednávku.
    const ordersQuery = useQuery({
        ...QueryApi.Customer.queries.Orders({ offset: 0, limit: 1 }),
        enabled: firstOrderDiscountEnabled && !!customerQuery.data,
    });
    const paymentMethodsQuery = useQuery(QueryApi.PaymentMethods.queries.PaymentMethods);
    const firstOrderDiscount =
        firstOrderDiscountEnabled && !!customerQuery.data && ordersQuery.data?.total === 0
            ? Math.round(total * 0.2 * 100) / 100
            : 0;
    const displayTotal = total - firstOrderDiscount;
    const [timeType, timeStart, timedDate, paymentMethodId, deliveryMethodId] = useWatch({
        control: formMethods.control,
        name: [
            'time.type',
            'time.timed.timeStart',
            'time.timed.date',
            'paymentMethod.id',
            'deliveryMethod.id',
        ],
    });
    const selectedPaymentMethod = paymentMethodsQuery.data?.find((method) => method.id === paymentMethodId);
    const applicableTips = useMemo(
        () => getApplicableTips(selectedPaymentMethod, deliveryMethodId),
        [selectedPaymentMethod, deliveryMethodId],
    );
    const isTimedOrderComplete = timeType === 'timed' && !!timeStart && !!timedDate;
    const showClosedOrderButton =
        !createOrderMutation.isPending && isVenueAvailabilityReady && !isVenueOpen && !isTimedOrderComplete;
    const hasTimedOrders = !!selectedDeliveryMethod?.extensionTimedOrders;
    const reopenWhenText = getVenueReopenWhenText({
        status: venueAvailability,
        locale,
        t,
        closedKeys: {
            today: 'CART_SUMMARY_BOX.WHEN_TODAY_AT',
            tomorrow: 'CART_SUMMARY_BOX.WHEN_TOMORROW_AT',
            later: 'CART_SUMMARY_BOX.WHEN_ON_DAY_AT',
        },
        outageWhenKeys: {
            today: 'CART_SUMMARY_BOX.WHEN_TODAY_AT',
            tomorrow: 'CART_SUMMARY_BOX.WHEN_TOMORROW_AT',
            minutes: 'CART_SUMMARY_BOX.WHEN_IN_MINUTES',
            soon: 'CART_SUMMARY_BOX.WHEN_SOON',
            untilDate: 'CART_SUMMARY_BOX.WHEN_UNTIL_DATE',
        },
    });
    let closedCtaLabel: string | undefined;
    if (showClosedOrderButton) {
        // Informational only — do not use action-shaped "Schedule order" on a disabled button.
        if (reopenWhenText) {
            closedCtaLabel = t('CART_SUMMARY_BOX.CANNOT_ORDER_CLOSED_WITH_TIME', { when: reopenWhenText });
        } else {
            closedCtaLabel = t('CART_SUMMARY_BOX.CANNOT_ORDER_CLOSED');
        }
    }
    const intervalMinutes = selectedDeliveryMethod?.extensionTimedOrders?.timeIntervalMinutes;
    let deliverAtLabel: string | undefined;
    if (isTimedOrderComplete && timeStart && timedDate && intervalMinutes) {
        const whenStart = dayjs(`${timedDate}T${timeStart}`);
        const whenEnd = whenStart.add(intervalMinutes, 'minutes');
        const timeRange = {
            timeStart: getLocaleTime(whenStart.toDate(), locale),
            timeEnd: getLocaleTime(whenEnd.toDate(), locale),
        };
        const dayDiff = whenStart.startOf('day').diff(dayjs().startOf('day'), 'day');
        if (dayDiff <= 0) {
            deliverAtLabel = t('CART_SUMMARY_BOX.DELIVER_AT', timeRange);
        } else if (dayDiff === 1) {
            deliverAtLabel = t('CART_SUMMARY_BOX.DELIVER_AT_TOMORROW', timeRange);
        } else {
            deliverAtLabel = t('CART_SUMMARY_BOX.DELIVER_AT_ON_DAY', {
                dayOn: t(`DAYS_ON.${whenStart.day()}` as MessageKey),
                date: getLocaleDate(whenStart.toDate(), locale),
                ...timeRange,
            });
        }
    }

    // Create totalPrice object for backward compatibility
    const totalPrice = useMemo(
        () => ({
            currency,
            itemsSum,
            packagesSum,
            subtotal,
            deliveryPrice: deliveryPrice.total,
            total,
            rounding,
            tipsCalculation,
            tipsTotal,
            topUpFee,
            paymentMethodFee,
        }),
        [
            currency,
            itemsSum,
            packagesSum,
            subtotal,
            deliveryPrice.total,
            total,
            rounding,
            tipsCalculation,
            tipsTotal,
            topUpFee,
            paymentMethodFee,
        ],
    );

    const valuesTable: SummaryItemT[] = useMemo(() => {
        return [
            {
                name: t('CART_SUMMARY_BOX.SUBTOTAL'),
                price: {
                    amount: totalPrice.itemsSum,
                    currency: totalPrice.currency,
                },
                type: 'subtotal',
            },
            ...(() => {
                const isDeliveryType = selectedDeliveryMethod
                    ? isDeliveryMethod(selectedDeliveryMethod)
                    : false;
                const baseAmount = deliveryPrice.base;
                const areaAmount = deliveryPrice.area;
                const rows: SummaryItemT[] = [];

                if (baseAmount > 0) {
                    rows.push({
                        name: isDeliveryType
                            ? t('CART_SUMMARY_BOX.DELIVERY_FEE')
                            : t('CART_SUMMARY_BOX.TAKEAWAY'),
                        price: {
                            amount: baseAmount,
                            currency: selectedCurrency,
                        },
                        type: 'delivery',
                    });
                }

                if (areaAmount > 0) {
                    rows.push({
                        name: t('CART_SUMMARY_BOX.ZONE_FEE'),
                        price: {
                            amount: areaAmount,
                            currency: selectedCurrency,
                        },
                        type: 'zone-fee',
                    });
                }

                // If neither has a price but delivery is selected with areas, show as free
                if (rows.length === 0 && totalPrice.deliveryPrice > 0) {
                    rows.push({
                        name: isDeliveryType
                            ? t('CART_SUMMARY_BOX.DELIVERY')
                            : t('CART_SUMMARY_BOX.TAKEAWAY'),
                        price: {
                            amount: totalPrice.deliveryPrice,
                            currency: selectedCurrency,
                        },
                        type: 'delivery',
                    });
                }

                return rows;
            })(),
            ...(totalPrice.paymentMethodFee > 0
                ? [
                      {
                          name: t('CART_SUMMARY_BOX.PAYMENT_FEE'),
                          price: {
                              amount: totalPrice.paymentMethodFee,
                              currency: totalPrice.currency,
                          },
                          type: 'payment-fee',
                      },
                  ]
                : []),
            ...(totalPrice.packagesSum > 0
                ? [
                      {
                          name: t('CART_SUMMARY_BOX.PACKAGING'),
                          price: {
                              amount: totalPrice.packagesSum,
                              currency: totalPrice.currency,
                          },
                          type: 'packaging',
                      },
                  ]
                : []),
            ...(totalPrice.topUpFee > 0
                ? [
                      {
                          name: t('CART_SUMMARY_BOX.MIN_ORDER_PRICE_ADDITIONAL_PAYMENT'),
                          price: {
                              amount: totalPrice.topUpFee,
                              currency: totalPrice.currency,
                          },
                          type: 'min-order-price-additional-payment',
                      },
                  ]
                : []),
            ...applicableTips.flatMap((tip) => {
                const amount = totalPrice.tipsCalculation[tip.id];
                if (!amount) return [];
                return [
                    {
                        name: localize(tip.name, locale),
                        price: {
                            amount,
                            currency: totalPrice.currency,
                        },
                        type: 'tip',
                        tipId: tip.id,
                    },
                ];
            }),
            ...(totalPrice.rounding !== 0
                ? [
                      {
                          name: t('CART_SUMMARY_BOX.ROUNDING'),
                          price: {
                              amount: totalPrice.rounding,
                              currency: totalPrice.currency,
                          },
                          type: 'rounding',
                      },
                  ]
                : []),
            // TODO: Remove — dočasná sleva na první objednávku.
            ...(firstOrderDiscount > 0
                ? [
                      {
                          name: 'Sleva na první objednávku (−20 %)',
                          price: {
                              amount: -firstOrderDiscount,
                              currency: selectedCurrency,
                          },
                          type: 'discount',
                      },
                  ]
                : []),
        ];
    }, [
        t,
        totalPrice,
        selectedCurrency,
        deliveryPrice.area,
        deliveryPrice.base,
        selectedDeliveryMethod,
        applicableTips,
        locale,
        // TODO: Remove — dočasná sleva na první objednávku.
        firstOrderDiscount,
    ]);

    const handleSendOrder = async () => {
        // TODO: formValues from handleSubmit sometimes doesn't have paymentMethod
        const formValues = formMethods.getValues();
        if (!formValues.deliveryMethod.id) {
            toast(({ closeToast }) => (
                <Alert
                    heading={t('CART_SUMMARY_BOX.DELIVERY_METHOD_REQUIRED')}
                    type="error"
                    onClose={closeToast}
                />
            ));
            return;
        }
        const availability = selectedDeliveryMethod ? getAvailability(selectedDeliveryMethod) : null;
        if (availability?.isAddressRequired) {
            toast(({ closeToast }) => (
                <Alert heading={t('CART_DELIVERY_BOX.ADDRESS_REQUIRED')} type="error" onClose={closeToast} />
            ));
            return;
        }
        if (availability?.isUndeliverable) {
            toast(({ closeToast }) => (
                <Alert
                    heading={
                        availability.areaTimeRestrictionLabel
                            ? t('CART_DELIVERY_BOX.ZONE_UNAVAILABLE_TRY_OTHER')
                            : t('CART_DELIVERY_BOX.ADDRESS_UNDELIVERABLE')
                    }
                    type="error"
                    onClose={closeToast}
                />
            ));
            return;
        }
        if (availability?.isTimeRestricted) {
            toast(({ closeToast }) => (
                <Alert
                    heading={t('CART_SUMMARY_BOX.DELIVERY_METHOD_UNAVAILABLE')}
                    type="error"
                    onClose={closeToast}
                />
            ));
            return;
        }
        if (availability?.isMinOrderDisabled) {
            toast(({ closeToast }) => (
                <Alert heading={t('CART_SUMMARY_BOX.MIN_ORDER_NOT_MET')} type="error" onClose={closeToast} />
            ));
            return;
        }
        if (!formValues.paymentMethod.id) {
            toast(({ closeToast }) => (
                <Alert
                    heading={t('CART_SUMMARY_BOX.PAYMENT_METHOD_REQUIRED')}
                    type="error"
                    onClose={closeToast}
                />
            ));
            return;
        }
        if (
            verifyTelephoneEnabled &&
            !getCachedTelephoneVerified(formValues.contactInfo.telephone, {
                isLoggedIn: !!customerQuery.data,
                customerTelephone: customerQuery.data?.telephone
                    ? `${customerQuery.data.telephonePrefix} ${customerQuery.data.telephone}`
                    : null,
                fallbackToLocal: true,
            })
        ) {
            toast(({ closeToast }) => (
                <Alert
                    heading={t('CART_SUMMARY_BOX.PHONE_VERIFICATION_REQUIRED')}
                    type="error"
                    onClose={closeToast}
                />
            ));
            return;
        }

        // Guard against stale cart items: an item (or its selected variant) may have been
        // removed from the menu since it was added — building the order payload below would
        // then read `foundItem.item` on `undefined` and crash mid-submit. Abort with a clear
        // message instead so the customer can refresh the cart.
        const hasStaleItems = formValues.items.some((item) => {
            const itemInMenu = !!itemsLookup[item.hash];
            const variantInMenu = !item.variant || !!itemsLookup[item.variant];
            return !itemInMenu || !variantInMenu;
        });
        if (hasStaleItems) {
            toast(({ closeToast }) => (
                <Alert heading={t('CART_SUMMARY_BOX.ITEMS_UNAVAILABLE')} type="error" onClose={closeToast} />
            ));
            return;
        }

        const [telephonePrefix, telephone] = formValues.contactInfo.telephone.split(' ');
        const firebaseDeviceToken = await getFirebaseDeviceToken();
        const packageItemById = buildPackageItemByIdMap(cartMenuData?.menuLists);

        const orderPayload = {
            customerId: customerQuery.data?.id,
            address: formValues.deliveryMethod.address
                ? {
                      addressId: null,
                      // The cart form stores `country` as a plain string while the order
                      // input expects the SDK `Country` enum; the values are identical
                      // country codes, so we reconcile the nominal type here.
                      address: formValues.deliveryMethod.address as unknown as AddressInputDto,
                  }
                : null,
            contactInfo: {
                contactInfo: {
                    email: formValues.contactInfo.email,
                    newsletterApproved: formValues.newsletterAgreement,
                    telephonePrefix,
                    telephone,
                },
            },
            personalInfo: {
                name: formValues.contactInfo.name,
                surname: formValues.contactInfo.surname,
                preferredLang: (customerQuery.data?.preferredLang ??
                    locale.toUpperCase().replace('-', '_')) as Language,
                preferredCurrency: selectedCurrency,
            },
            scheduledAt:
                formValues.time.type === 'timed' &&
                formValues.time.timed?.date &&
                formValues.time.timed.timeStart
                    ? dayjs(`${formValues.time.timed.date}T${formValues.time.timed.timeStart}`).toISOString()
                    : null,
            currency: selectedCurrency,
            deliveryMethodId: formValues.deliveryMethod.id!,
            items: formValues.items.map((item) => {
                const foundItem = itemsLookup[item.hash];
                if (!foundItem) {
                    throw new Error('Stale cart item passed validation');
                }
                const foundVariant = item.variant ? itemsLookup[item.variant] : null;

                const addonDefinitions =
                    (foundVariant?.body.addons?.length ? foundVariant.body.addons : foundItem.body.addons) ??
                    [];
                const userLanguage = locale.toUpperCase().replace('-', '_') as Language;
                const selectedAddons = mapCartAddonsToOrderAddons(
                    item.addons,
                    addonDefinitions,
                    itemsLookup,
                    getItemPrices,
                    cartMenuData ?? undefined,
                    selectedDeliveryMethod?.deliveryMethodDefinitionId,
                    userLanguage,
                    item.memberVariants,
                );

                const isDefaultVariantSelected =
                    foundVariant?.placement.type === 'variant' && foundVariant.placement.isDefault === true;

                const packagesHash =
                    foundVariant && !isDefaultVariantSelected ? (item.variant ?? item.hash) : item.hash;
                const packagesSourceItem = cartMenuData?.items[packagesHash]?.item;
                const mainItemPackages =
                    packagesSourceItem &&
                    shouldIncludePackages(
                        packagesSourceItem,
                        selectedDeliveryMethod?.deliveryMethodDefinitionId,
                    )
                        ? (packagesSourceItem.packages
                              ?.map((pkg) =>
                                  // Prefer the embedded packageItem only when it carries prices; otherwise
                                  // fall back to the itemRefs copy. A default-variant package can embed
                                  // only a name stub, which would otherwise render without a price.
                                  enrichPackageWithPriceableItem(pkg, packageItemById),
                              )
                              .filter((pkg) =>
                                  isPackageApplicable(
                                      pkg,
                                      selectedDeliveryMethod?.deliveryMethodDefinitionId,
                                      userLanguage,
                                  ),
                              )
                              .map((pkg) => {
                                  if (!pkg.packageItem) {
                                      return null;
                                  }
                                  const priceValue = getItemPrices(pkg.packageItem);
                                  return priceValue?.itemPrice.amount
                                      ? {
                                            id: pkg.id,
                                            price: priceValue.itemPrice.amount,
                                            item: {
                                                name: pkg.packageItem.name,
                                                trait: pkg.packageItem.trait ?? '',
                                            },
                                        }
                                      : null;
                              })
                              .filter((pkg): pkg is NonNullable<typeof pkg> => pkg !== null) ?? [])
                        : [];

                const priceHash = item.variant || item.hash;
                const itemPrices = getItemPrices(priceHash);

                return {
                    itemId: foundItem.body.id,
                    quantity: `${item.quantity}`,
                    price: itemPrices?.itemPrice.amount,
                    note: item.note,
                    selectedAddons,
                    selectedVariantId: foundVariant?.placement.variantItemId,
                    packages: mainItemPackages.length > 0 ? mainItemPackages : undefined,
                };
            }),
            // The amount the payment is for. A payment integration charges exactly this
            // (a terminal puts it on its display), so it is the real total — the same one
            // sent as `finalPrice`, fees and tips included, discount already netted.
            payments: [{ paymentMethodId: formValues.paymentMethod.id!, amount: `${displayTotal}` }],
            delivery: totalPrice.deliveryPrice > 0 ? { customerCharge: `${totalPrice.deliveryPrice}` } : null,
            apiPartnerSettingsId: venueQuery.data?.apsId,
            country: Country.CZ,
            // TODO: Remove — dočasná sleva na první objednávku (displayTotal already nets the discount).
            finalPrice: `${displayTotal}`,
            itemsPrice: `${totalPrice.itemsSum}`,
            note: formValues.note,
            priceModifiers: [
                ...(deliveryPrice.base > 0
                    ? [
                          {
                              type: OrderPriceModifierType.DELIVERY,
                              amount: `${deliveryPrice.base}`,
                              snapshot: {},
                              deliveryMethodInfoId: selectedDeliveryMethod!.deliveryMethodInfoId,
                          },
                      ]
                    : []),
                ...(deliveryPrice.area > 0 && matchedArea
                    ? [
                          {
                              type: OrderPriceModifierType.DELIVERY,
                              amount: `${deliveryPrice.area}`,
                              snapshot: {},
                              deliveryMethodInfoId: matchedArea.deliveryMethodInfoId,
                          },
                      ]
                    : []),
                // `tipId` lets the BE resolve the tip's VAT rate and localized name snapshot.
                // The ROUNDED amount is sent — a fractional tip breaks the POS (OMP-2166).
                ...applicableTips.flatMap((tip) => {
                    const amount = totalPrice.tipsCalculation[tip.id];
                    if (!amount) return [];
                    return [
                        {
                            type: OrderPriceModifierType.TIP,
                            amount: `${amount}`,
                            snapshot: {},
                            tipId: tip.id,
                        },
                    ];
                }),
                ...(totalPrice.paymentMethodFee > 0
                    ? [
                          {
                              type: OrderPriceModifierType.PAYMENT,
                              amount: `${totalPrice.paymentMethodFee}`,
                              snapshot: {},
                              paymentMethodId: formValues.paymentMethod.id!,
                          },
                      ]
                    : []),
                ...(totalPrice.rounding !== 0
                    ? [
                          {
                              type: OrderPriceModifierType.ROUNDING,
                              amount: `${totalPrice.rounding}`,
                              snapshot: {},
                          },
                      ]
                    : []),
                ...(totalPrice.topUpFee > 0
                    ? [
                          {
                              type: OrderPriceModifierType.MIN_ORDER_SURCHARGE,
                              amount: `${totalPrice.topUpFee}`,
                              snapshot: {},
                          },
                      ]
                    : []),
                // TODO: Remove — dočasná sleva na první objednávku.
                ...(firstOrderDiscount > 0
                    ? [
                          {
                              type: OrderPriceModifierType.DISCOUNT,
                              amount: `${-firstOrderDiscount}`,
                              snapshot: {},
                              name: 'Sleva na první objednávku (−20 %)',
                          },
                      ]
                    : []),
            ],
            ...(firebaseDeviceToken
                ? {
                      metadata: {
                          eshop: {
                              firebaseDeviceToken,
                          },
                      },
                  }
                : {}),
        };

        let order: Awaited<ReturnType<typeof createOrderMutation.mutateAsync>>;
        try {
            order = await createOrderMutation.mutateAsync(orderPayload);
        } catch (err) {
            const code = err instanceof Error ? err.message : '';
            const errorMessages: Partial<Record<string, Parameters<typeof t>[0]>> = {
                VENUE_OUTAGE_ACTIVE: 'CART_SUMMARY_BOX.VENUE_OUTAGE_ACTIVE',
                INVALID_SCHEDULED_AT: 'CART_SUMMARY_BOX.ORDER_FAILED',
                DELIVERY_METHOD_REQUIRED: 'CART_SUMMARY_BOX.DELIVERY_METHOD_REQUIRED',
                DELIVERY_ADDRESS_REQUIRED: 'CART_SUMMARY_BOX.DELIVERY_METHOD_REQUIRED',
                PAYMENT_METHOD_REQUIRED: 'CART_SUMMARY_BOX.PAYMENT_METHOD_REQUIRED',
                DELIVERY_ADDRESS_UNDELIVERABLE: 'CART_SUMMARY_BOX.DELIVERY_ADDRESS_UNDELIVERABLE',
                TELEPHONE_NOT_VERIFIED: 'CART_SUMMARY_BOX.PHONE_VERIFICATION_REQUIRED',
                PAYMENT_GATEWAY_FAILED: 'CART_SUMMARY_BOX.ORDER_FAILED',
            };
            const translationKey = errorMessages[code];
            toast(({ closeToast }) => (
                <Alert
                    heading={translationKey ? t(translationKey) : t('CART_SUMMARY_BOX.ORDER_FAILED')}
                    type="error"
                    onClose={closeToast}
                />
            ));
            return;
        }

        if (!order) {
            return;
        }

        // Order create succeeded for non-gateway paths. Failures after this (tracking /
        // analytics) must not look like "order failed". Gateway init failure throws earlier
        // (PAYMENT_GATEWAY_FAILED) and cancels the order before we get here.
        const createdOrder = order;
        try {
            setTrackedOrders((trackedOrders) => {
                if (!createdOrder.id || trackedOrders.some((tracked) => tracked.id === createdOrder.id)) {
                    return trackedOrders;
                }
                return [...trackedOrders, { id: createdOrder.id, isCompleted: false }];
            });
            // Seed ['ORDER', id] so the landing page paints immediately after navigation.
            //
            // The create response is a NARROWER projection than the OrderApiDto getOrder
            // reads back — it carries no `status`, `deliveryMethod`, `payments` or
            // `contactInfo`. That is why this is a partial write: the query declares no
            // staleTime, so the landing page refetches on mount and fills the rest in.
            // Anything reading this entry must tolerate those fields being absent.
            const { verifiedPhoneToken: _, ...cachedOrder } = createdOrder;
            queryClient.setQueryData(
                QueryApi.Order.queries.Order(createdOrder.id).queryKey,
                (previous) => ({ ...previous, ...cachedOrder }) as OrderApiDto,
            );
            EventService.trigger('order:view-alert-tracking', { orderId: createdOrder.id });
        } catch {
            // Order tracking is best-effort; the order itself already succeeded.
        }

        formMethods.reset(
            getCartFormDefaultValues(
                formValues.saveForNextOrder
                    ? { ...formValues, _isHydrated: true, _isOrderCreated: true }
                    : { _isHydrated: true, _isOrderCreated: true },
            ),
        );

        // A payment taken on a terminal leaves the order AWAITING_PAYMENT, and the customer
        // is routed to the landing page while the terminal still holds it. That page treats an
        // order created in this session as a success until its first status fetch answers, so
        // it flashed the thank-you page at a customer whose card had not been charged — seed
        // the status it is about to fetch, so its very first frame says "awaiting payment".
        if ('paymentPending' in createdOrder && createdOrder.paymentPending) {
            queryClient.setQueryData(
                getOrderLandingStatusQueryKey(createdOrder.id),
                OrderStatusDefinitionIdentifier.AWAITING_PAYMENT,
            );
        }

        // Server already allowlist-sanitized paymentRedirectUrl in createOrder.
        if ('paymentRedirectUrl' in createdOrder && createdOrder.paymentRedirectUrl) {
            window.location.href = createdOrder.paymentRedirectUrl;
            return;
        }

        router.push(getPageUrl('order', { ORDER_ID: createdOrder.id }));
    };

    return (
        <Layout
            element={Card}
            {...props}
            className={cn('cart-summary-box', props.className)}
            data-testid="cart-summary-box"
        >
            {isDetailsVisible && (
                <button
                    type="button"
                    className="cart-summary-box__toggle-button"
                    onClick={() => setIsDetailsVisible(false)}
                    data-testid="cart-summary-toggle-button"
                >
                    <Icon name="arrow-down" width={24} height={24} />
                </button>
            )}
            <div className="cart-summary-box__content">
                {isDetailsVisible && (
                    <h3 className="cart-summary-box__title" data-testid="cart-summary-title">
                        {t('CART_SUMMARY_BOX.TITLE')}
                    </h3>
                )}
                <table className="cart-summary-box__table">
                    <tbody>
                        {isDetailsVisible ? (
                            <>
                                {valuesTable.map((summaryItem, index) => (
                                    <CartSummaryBoxItem
                                        key={index}
                                        {...summaryItem}
                                        isLast={
                                            index === valuesTable.length - 1 && DISCOUNTS_MOCK.length === 0
                                        }
                                    />
                                ))}
                                {([] as SummaryItemT[]).map((summaryItem, index) => (
                                    <CartSummaryBoxItem
                                        key={index}
                                        {...summaryItem}
                                        isFirst={index === 0}
                                        isLast={index === DISCOUNTS_MOCK.length - 1}
                                        isDiscount
                                    />
                                ))}
                                {/* <tr className="cart-summary-box__row cart-summary-box__row--bonus-points">
                                    <th align="left" data-testid="cart-summary-loyalty-points-label">
                                        {t('CART_SUMMARY_BOX.LOYALTY_POINTS_LABEL')}
                                    </th>
                                    <td align="right">
                                        <CartSummaryBoxBonusPoints amount={23} />
                                    </td>
                                </tr> */}
                            </>
                        ) : (
                            <tr>
                                <th align="left">
                                    <button
                                        className="cart-summary-box__details-button"
                                        onClick={() => setIsDetailsVisible(true)}
                                        data-testid="cart-summary-show-details-button"
                                    >
                                        {t('CART_SUMMARY_BOX.SHOW_DETAILS_BUTTON')}
                                    </button>
                                </th>
                                <td
                                    align="right"
                                    className="cart-summary-box__discount-text"
                                    data-testid="cart-summary-discount-text"
                                >
                                    {/*t('CART_SUMMARY_BOX.DISCOUNTS_TEXT', {
                                        amount: getPriceValueTranslation({
                                            t,
                                            amount: -220,
                                            currency: Currency.CZK,
                                        }),
                                    })*/}
                                </td>
                            </tr>
                        )}
                        <tr className="cart-summary-box__row">
                            <th align="left">
                                <span data-testid="cart-summary-total-label">
                                    {t('CART_SUMMARY_BOX.TOTAL_LABEL')}{' '}
                                </span>
                                <span
                                    className="cart-summary-box__total-note"
                                    data-testid="cart-summary-total-note"
                                >
                                    {t('CART_SUMMARY_BOX.TOTAL_NOTE')}
                                </span>
                            </th>
                            <td
                                align="right"
                                className="cart-summary-box__total-price"
                                data-testid="cart-summary-total-price"
                                data-amount={displayTotal}
                                data-currency={selectedCurrency}
                            >
                                <As
                                    as={isDetailsVisible ? Fragment : 'div'}
                                    className="cart-summary-box__total-wrapper"
                                >
                                    <AnimatedPrice amount={displayTotal} currency={selectedCurrency} />
                                    {/* !isDetailsVisible && <CartSummaryBoxBonusPoints amount={23} /> */}
                                </As>
                            </td>
                        </tr>
                    </tbody>
                </table>
                {!isVenueOpen && hasTimedOrders && !isTimedOrderComplete && reopenWhenText && (
                    <p className="cart-summary-box__closed-microcopy" data-testid="cart-closed-microcopy">
                        {venueAvailability.kind === VenueAvailabilityKind.Outage
                            ? t('CART_SUMMARY_BOX.OUTAGE_MICROCOPY', {
                                  reason:
                                      getLocalizedValue(venueAvailability.description ?? {}) ||
                                      t('NAVBAR.OUTAGE_LEAD'),
                                  when: reopenWhenText,
                              })
                            : t('CART_SUMMARY_BOX.CLOSED_MICROCOPY', { when: reopenWhenText })}
                    </p>
                )}
                {deliverAtLabel && (
                    <p className="cart-summary-box__deliver-at" data-testid="cart-deliver-at">
                        {deliverAtLabel}
                    </p>
                )}
                <Button
                    data-testid="cart-summary-confirm-order-button"
                    onClick={formMethods.handleSubmit(handleSendOrder)}
                    disabled={!(isVenueOpen || isTimedOrderComplete) || createOrderMutation.isPending}
                >
                    {closedCtaLabel ??
                        t.rich('CART_SUMMARY_BOX.CONFIRM_ORDER_BUTTON', {
                            amount: () => <AnimatedPrice amount={displayTotal} currency={selectedCurrency} />,
                        })}
                </Button>
            </div>
        </Layout>
    );
};
